import { useEffect, useRef, useState } from "react";

import { cn } from "../lib/cn";

/**
 * A thin page-load bar fixed to the top of the window (like YouTube / GitHub). While `active`, it creeps towards 90 %
 * so the visitor sees the click registered; when `active` turns false it fills to 100 % and fades out. Purely visual
 * (`aria-hidden`): pass `label` to announce "Loading" politely to screen readers instead.
 */
export function PageLoadBar({
  active,
  label,
  tone = "ink",
  className,
}: {
  active: boolean;
  /** `ink` (black) on a yellow header like the storefront's; `brand` (yellow) over dark or white chrome (panels). */
  tone?: "ink" | "brand";
  /** Announced (aria-live) while loading, e.g. "Loading page". */
  label?: string;
  className?: string;
}) {
  const [phase, setPhase] = useState<"idle" | "start" | "running" | "done">("idle");
  const timers = useRef<number[]>([]);

  useEffect(() => {
    const clear = () => {
      timers.current.forEach((id) => window.clearTimeout(id));
      timers.current = [];
    };
    const later = (fn: () => void, ms: number) => {
      timers.current.push(window.setTimeout(fn, ms));
    };
    clear();
    if (active) {
      // two steps so the width transition starts from 0 on every navigation
      later(() => setPhase("start"), 0);
      later(() => setPhase("running"), 30);
    } else {
      later(() => setPhase((current) => (current === "idle" ? "idle" : "done")), 0);
      later(() => setPhase("idle"), 450);
    }
    return clear;
  }, [active]);

  const width = { idle: "0%", start: "0%", running: "90%", done: "100%" }[phase];
  return (
    <>
      <div
        aria-hidden
        className={cn(
          "pointer-events-none fixed inset-x-0 top-0 z-[60] h-[3px]",
          phase === "idle" && "opacity-0",
          className,
        )}
      >
        <div
          className={cn(
            "h-full",
            tone === "ink"
              ? "bg-ink shadow-[0_0_8px_rgba(17,17,17,0.45)]"
              : "bg-brand shadow-[0_0_8px_rgba(255,212,0,0.6)]",
            phase === "running" && "transition-[width] duration-[8000ms] ease-out",
            phase === "done" &&
              "opacity-0 transition-[width,opacity] duration-300 ease-out [transition-delay:0ms,150ms]",
          )}
          style={{ width }}
        />
      </div>
      {label && (
        <span className="sr-only" aria-live="polite">
          {active ? label : ""}
        </span>
      )}
    </>
  );
}
